// The signed-in user's account (D48): who you are, changing your password, and signing out.
// Layout after Isomorphic's account-settings as the mocks use it (a label column and a form
// column per section; design/isomorphic/src/pages/account.tsx). There is no session list in
// the API yet, so Sessions explains what a password change does and signs this browser out.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Button } from "rizzui";

import { meQuery, signOutAndLeave } from "../auth/api";
import { useT } from "../i18n";
import { PasswordForm } from "../auth/PasswordForm";
import { sectionCrumbs, SECTIONS } from "../layout/nav";
import { toast } from "../layout/toast";
import { Avatar } from "../ui/Avatar";
import { WidgetCard } from "../ui/Card";
import { PageHeader } from "../ui/PageHeader";
import { Pill } from "../ui/status";
import { day } from "./format";

function Row({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <section className="grid gap-4 border-b border-muted py-6 last:border-0 md:grid-cols-[260px_1fr] md:gap-8" aria-label={title}>
      <div>
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        <p className="mb-0 mt-1 text-[13px] text-gray-500">{hint}</p>
      </div>
      <div className="max-w-xl">{children}</div>
    </section>
  );
}

export default function AccountPage() {
  const t = useT().account.page;
  const me = useQuery(meQuery());
  const queryClient = useQueryClient();
  const user = me.data;

  return (
    <>
      <PageHeader title={SECTIONS.account.title} breadcrumb={sectionCrumbs("account")} />
      <WidgetCard className="!py-0" label={t.label}>
        <Row title={t.profile} hint={t.profileHint}>
          {user ? (
            <div className="flex items-center gap-4">
              <Avatar email={user.email} size="lg" />
              <div className="min-w-0">
                <div className="truncate font-semibold text-gray-900">{user.email}</div>
                <div className="text-[13px] text-gray-500">{t.added(day(user.created_at))}</div>
                <Pill tone={user.role === "owner" ? "primary" : "gray"} className="mt-1.5 capitalize">
                  {user.role}
                </Pill>
              </div>
            </div>
          ) : null}
        </Row>
        <Row title={t.changePassword} hint={t.changePasswordHint}>
          <PasswordForm
            onChanged={async () => {
              toast(t.passwordChanged);
              await queryClient.invalidateQueries({ queryKey: ["me"] });
            }}
          />
          {user ? <p className="mb-0 mt-4 text-xs text-gray-500">{t.passwordLastChanged(day(user.password_changed_at))}</p> : null}
        </Row>
        <Row title={t.sessions} hint={t.sessionsHint}>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-muted p-3.5">
            <div>
              <div className="font-medium text-gray-900">{t.thisBrowser}</div>
              <div className="text-xs text-gray-500">{t.cookie}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => void signOutAndLeave()}>
              {t.signOut}
            </Button>
          </div>
        </Row>
      </WidgetCard>
    </>
  );
}
